import debounce from 'lodash/debounce';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import config from '../../../../app.config.json';
import { selectUser } from '../reducers/user';
import axios, { API_URL } from '../services/axios';
import { MinimalPassportApplication, MortgageErrorStatus, MortgageProductResult } from '../types/mortgages';
import { Broker, Property } from '../types/property';
import floatToCurrency, { parseNumber } from '../utils/floatToCurrency';
import {
  getDefaultDeposit,
  getDefaultTerm,
  getDepositPercentage,
} from '../utils/property/getMortgageCalculatorDefaults';
import useApi from './useApi';

export interface MortgageCalculatorFormValues {
  paymentTerm: number;
  propertyValue?: number;
  deposit: number;
}

export interface GetMortgageCalculator {
  values: MortgageCalculatorFormValues;
  defaultTerm: MortgageCalculatorFormValues;
  passportId?: string;
}

export type GetMortgageResult = (values: GetMortgageCalculator) => Promise<void>;

interface MortgageEvent {
  value: number;
  deposit: number;
  term: number;
  fieldModified?: string;
  depositFromCookie: boolean;
  termFromCookie: boolean;
}

export interface CTAText {
  heading: string;
  subHeading: string;
  text: string;
  mobileText: string;
  link: string;
}

export interface MortgageCalcData {
  data?: MortgageProductResult;
  error: any;
  loading: boolean;
  sending: boolean;
  errorStatus?: MortgageErrorStatus;
  hasResult: boolean;
  mortgagePayment: string;
  depositPercentage?: number;
  hasMortgageCalculator: boolean;
  hasBrokerCalculator: boolean;
  getMortgageResult: (values: MortgageCalculatorFormValues) => void;
  logMortgageEvent: (event: MortgageEvent) => void;
  submitLead: (broker: Broker, values: any) => Promise<boolean>;
  ctaText: CTAText;
}

const COMMENT = 'Please confirm my Mortgage Eligibility';

const DEBOUNCE_TIME = process?.env?.JEST_WORKER_ID ? 0 : 1000;

const getError = (depositPercentage?: number, data?: MortgageProductResult): MortgageErrorStatus | undefined => {
  let eStatus: MortgageErrorStatus | undefined;

  if (data) {
    const { deposit, value, product } = data;

    if (deposit === value) {
      eStatus = { error: 'DEPOSIT_EQUAL_VALUE' };
    } else if (deposit > value) {
      eStatus = { error: 'DEPOSIT_GREATER_VALUE' };
    } else if (depositPercentage && depositPercentage > 99) {
      eStatus = { error: 'INVALID_LTV' };
    } else if (product == null) {
      eStatus = { error: 'NO_RESULTS' };
    }
  }

  return eStatus;
};

export const getCTAText = (passport?: MinimalPassportApplication | null): CTAText => {
  const firstName = passport?.names?.length ? passport.names[0] : '';

  if (passport) {
    if (!passport.bookingStatus && !passport.timeCompleted) {
      return {
        heading: `${
          firstName ? `${firstName}, you` : 'You'
        } can finish your mortgage pre-approval process by clicking below.`,
        subHeading: '',
        text: 'Complete your mortgage pre-approval process',
        mobileText: 'Complete process',
        link: `/mortgages/pre-approval/${passport.step || 1}`,
      };
    }

    if (passport.timeCompleted && (!passport.bookingStatus || passport.bookingStatus === 'CANCELLED')) {
      return {
        heading: `${
          firstName ? `${firstName}, now` : 'Now'
        } that you've been pre-approved it's time to talk to one of our mortgage experts to discuss your next steps.`,
        subHeading: '',
        text: 'Book a call now',
        mobileText: 'Book a call now',
        link: '/mortgages/booking',
      };
    }

    if (passport.timeCompleted && passport.bookingStatus === 'BOOKED') {
      return {
        heading: `${
          firstName ? `${firstName}, now` : 'Now'
        } that your pre-approval has been activated, you can show sellers you're serious and ready to buy.`,
        subHeading: '',
        text: 'Forward Pre-Approval',
        mobileText: 'Forward Pre-Approval',
        link: '/my/preapproval',
      };
    }
  }

  return {
    heading: 'Find out instantly if you are eligible for a mortgage...',
    subHeading: "and don't miss out on your dream home.",
    text: 'Get pre-approved for FREE in 5 minutes',
    mobileText: 'Get pre-approved for FREE',
    link: '/mortgages/pre-approval/1',
  };
};

const postBrokerLead = async (propertyId: number, brokerId: number, dispatch: Dispatch, data: any) => {
  try {
    await axios(
      {
        method: 'POST',
        url: `${API_URL}/property/${propertyId}/lead/broker/${brokerId}`,
        data,
      },
      dispatch,
    );

    return true;
  } catch {
    return false;
  }
};

const useMortgageCalculator = (property: Property): MortgageCalcData => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const noOfInteractions = useRef(0);
  const [data, error, loading, fetchProduct] = useApi<MortgageProductResult>('', false);
  const [sending, setSending] = useState(false);
  const mortgagePayment =
    data?.product && data.product.initialMonthlyPayment > 0
      ? floatToCurrency(data?.product?.initialMonthlyPayment)
      : '-';
  const depositPercentage = data ? getDepositPercentage(data.deposit, data.value) : undefined;
  const errorStatus = getError(depositPercentage, data);
  const hasResult = !errorStatus && !!data;
  const ctaText = getCTAText(data?.passportMinimal);
  const hasBrokerCalculator = !!(property.brokers && property.brokers.length > 0 && property.published);
  const hasMortgageCalculator =
    property.type === 'property' &&
    !property.development &&
    property.propertyType?.key === 'residential' &&
    property.saleType?.key === 'sale' &&
    property.countryCode === 'GBR' &&
    property.price !== undefined &&
    typeof property.price.price === 'number' &&
    property.price.priceType !== 'PUBLIC_AUCTIONS' &&
    property.status?.key !== 'sold' &&
    property.status?.key !== 'saleAgreed' &&
    Boolean(property.published);

  const debounceFetchResult = debounce(fetchProduct, DEBOUNCE_TIME, {
    leading: false,
    trailing: true,
  });

  const getMortgageResult: MortgageCalcData['getMortgageResult'] = (values) => {
    let url = `/mortgages/mortgage/product?value=${values.propertyValue}`;
    url += `&term=${values.paymentTerm}&defaultTerm=${getDefaultTerm(property)}`;
    url += `&deposit=${values.deposit}&defaultDeposit=${getDefaultDeposit(property)}`;

    if (user.profile?.currentPassportId) {
      url += `&passportId=${user.profile?.currentPassportId}`;
    }

    debounceFetchResult(url);
  };

  const submitLead: MortgageCalcData['submitLead'] = async (broker, values) => {
    let res = false;
    setSending(true);

    const mValues = {
      ...values,
      propertyValue: parseNumber(values.propertyValue),
      deposit: parseNumber(values.deposit),
      paymentTerm: parseNumber(values.paymentTerm),
    };

    if (broker.asssociatedAgents?.length) {
      const results = [];
      for (let i = 0; i < broker.asssociatedAgents.length; i += 1) {
        const mData = {
          ...mValues,
          interestRate: data?.product?.initialRate,
          contact: broker.asssociatedAgents[i],
          comment: COMMENT,
        };
        mData.contact = broker.asssociatedAgents[i];

        results.push(postBrokerLead(property.id, broker.id, dispatch, mData));
      }

      res = (await Promise.all(results))[0];
    } else {
      const mData = {
        ...mValues,
        interestRate: data?.product?.initialRate,
        contact: property.account.accountNumber,
        comment: COMMENT,
      };

      res = await postBrokerLead(property.id, broker.id, dispatch, mData);
    }

    setSending(false);
    return res;
  };

  const logMortgageEvent = async (payload: MortgageEvent) => {
    noOfInteractions.current += 1;

    await axios(
      {
        method: 'POST',
        url: `${config.apiUrl}/event`,
        data: {
          property: property.id,
          eventType: 'MORTGAGE_CALCULATOR_INTERACTION',
          mortgageCalculator: {
            sequence: noOfInteractions.current,
            outcome: data?.product ? 'Result' : 'Error',
            ...payload,
            deposit: parseNumber(payload.deposit),
            term: parseNumber(payload.term),
          },
        },
      },
      undefined,
    );
  };

  return {
    data,
    error,
    loading,
    getMortgageResult,
    mortgagePayment,
    depositPercentage,
    errorStatus,
    hasResult,
    hasMortgageCalculator,
    hasBrokerCalculator,
    logMortgageEvent,
    submitLead,
    sending,
    ctaText,
  };
};

export default useMortgageCalculator;
